<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minLength } from '@vuelidate/validators';
import { useInvitationsRepo } from '~/src/entities/invitation';

definePageMeta({
  layout: false,
});

const route = useRoute();
const invitationCode = route.query.invitation_code as string;

const { acceptOne, checkOne } = useInvitationsRepo();

const { data: invitation } = useAsyncData(() => checkOne(invitationCode));

const password = ref('');

const passwordRules = computed(() => ({
  password: {
    required: helpers.withMessage('Oops! It seems you forgot to enter your password.', required),
    minLength: helpers.withMessage('Your password needs to be at least 6 characters long. Can you make it a bit longer?', minLength(6)),
  },
}));

const passwordValidation = useVuelidate(passwordRules, { password });

async function handleAcceptInvitation() {
  if (invitation.value?.user_exists) {
    const existingUser = await acceptOne(invitationCode, { password: null });
    if (existingUser) {
      navigateTo('/sign-in');
      ElNotification({
        title: 'Success',
        message: 'You can login to your account now',
        type: 'success',
      });
    }
    else {
      ElNotification({
        title: 'Error',
        message: 'Failed to accept invitation',
        type: 'error',
      });
    }
  } else {
    const isValid = await passwordValidation.value.$validate();
    if (!isValid) {
      return;
    }
    const newMember = await acceptOne(invitationCode, { password: password.value });
    if (newMember) {
      navigateTo('/sign-in');
      ElNotification({
        title: 'Success',
        message: 'You can login to your account now',
        type: 'success',
      });
    }
    else {
      ElNotification({
        title: 'Error',
        message: 'Failed to accept invitation',
        type: 'error',
      });
    }
  }
}
</script>

<template>
  <div class=" max-h-screen lg:min-h-screen overflow-hidden">
    <div class="relative isolate overflow-hidden">
      <div class="mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-40 lg:flex lg:px-8 lg:pt-40 lg:pb-0">
        <div class="mx-auto max-w-2xl flex-shrink-0 lg:mx-0 lg:max-w-xl lg:pt-40">
          <h2 class="mx-auto max-w-2xl text-center text-3xl font-bold tracking-tight text-black sm:text-4xl">
            You were invited to join the team
          </h2>

          <template v-if="invitation?.user_exists">
            <p class="mt-6 text-lg leading-8 text-gray-500 text-center">
              Accept invitation and sign in to your account with {{ invitation?.invitee_email }}
            </p>
            <PButton class="mt-4 max-h-[42px] w-full" label="Join" @click="handleAcceptInvitation" />
          </template>

          <template v-else>
            <p class="mt-6 text-lg leading-8 text-gray-500 text-center">
              Get started by setting a new password for your account.
            </p>

            <div class="grid grid-cols-[auto_80px] gap-4 mt-4">
              <DfInputWrapper :errors="passwordValidation.password.$errors">
                <PPassword v-model="password" placeholder="Password" input-class="w-full" @keyup.enter="handleAcceptInvitation" />
              </DfInputWrapper>
              <PButton class="max-h-[42px]" label="Join" @click="handleAcceptInvitation" />
            </div>
          </template>

        </div>
        <div class="mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
          <div class="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
            <img src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=2301&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Office photo" width="2432" height="1442" class="w-[76rem] rounded-md bg-white/5 shadow-2xl ring-1 ring-white/10">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
